"use client";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Callout, Dialog, Flex, Text, VisuallyHidden } from "@radix-ui/themes";
import { getMeQueryKey, useUpdateMe } from "../api";
import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import * as Form from "@radix-ui/react-form";
import { DialogContent, Label, PrimaryButton, SecondaryButton, TextField, } from "./elements";
import { SaveButton } from "./save-button";
export function EditUserProfileDialog({ children, user, ...props }) {
    const [open, setOpen] = React.useState(false);
    const handleClose = React.useCallback(() => {
        setOpen(false);
    }, []);
    return (_jsxs(Dialog.Root, { ...props, open: props.open ?? open, onOpenChange: props.onOpenChange ?? setOpen, children: [_jsx(Dialog.Trigger, { children: children }), _jsx(DialogContent, { maxWidth: "480px", children: _jsx(Content, { user: user, onClose: handleClose }) })] }));
}
function Content({ user, onClose }) {
    const client = useQueryClient();
    const updateMe = useUpdateMe({
        mutation: {
            onSettled: () => {
                client.invalidateQueries({ queryKey: getMeQueryKey() });
            },
            onSuccess: (newProfile) => {
                client.setQueryData(getMeQueryKey(), {
                    ...user,
                    firstName: newProfile.firstName || user.firstName,
                    lastName: newProfile.lastName || user.lastName,
                });
            },
        },
    });
    return (_jsxs(_Fragment, { children: [_jsx(Dialog.Title, { mb: "5", children: "Edit name" }), _jsx(VisuallyHidden, { children: _jsxs(Dialog.Description, { children: ["Edit the details of ", _jsx(Text, { weight: "bold", children: user.email })] }) }), updateMe.error ? (_jsx(Callout.Root, { color: "red", my: "-2", children: _jsx(Callout.Text, { children: getMutationErrorMessage(updateMe.error) }) })) : null, _jsxs(Form.Root, { onSubmit: async (event) => {
                    event.preventDefault();
                    const formData = new FormData(event.currentTarget);
                    const firstName = formData.get("firstName")?.toString();
                    const lastName = formData.get("lastName")?.toString();
                    updateMe.mutate({
                        data: {
                            firstName: firstName ?? undefined,
                            lastName: lastName ?? undefined,
                        },
                    });
                }, children: [_jsxs(Flex, { my: "5", direction: "column", gap: "4", children: [_jsx(Form.Field, { name: "firstName", asChild: true, children: _jsxs(Flex, { direction: "column", gap: "1", children: [_jsx(Form.Label, { asChild: true, children: _jsx(Label, { children: "First name" }) }), _jsx(Form.Control, { asChild: true, children: _jsx(TextField, { "data-1p-ignore": true, autoComplete: "given-name", defaultValue: user.firstName ?? "", placeholder: "Your first name", disabled: updateMe.isPending || updateMe.isSuccess }) })] }) }), _jsx(Form.Field, { name: "lastName", asChild: true, children: _jsxs(Flex, { direction: "column", gap: "1", children: [_jsx(Form.Label, { asChild: true, children: _jsx(Label, { children: "Last name" }) }), _jsx(Form.Control, { asChild: true, children: _jsx(TextField, { "data-1p-ignore": true, autoComplete: "family-name", defaultValue: user.lastName ?? "", placeholder: "Your last name", disabled: updateMe.isPending || updateMe.isSuccess }) })] }) })] }), _jsxs(Flex, { mt: "5", gap: "3", justify: "end", children: [_jsx(Dialog.Close, { children: _jsx(SecondaryButton, { disabled: updateMe.isPending || updateMe.isSuccess, children: "Cancel" }) }), _jsx(SaveButton, { asChild: true, loading: updateMe.isPending, done: updateMe.isSuccess, onDone: onClose, children: _jsx(PrimaryButton, { children: "Save" }) })] }), _jsx(VisuallyHidden, { asChild: true, children: _jsx("section", { "aria-live": "polite", children: getMutationErrorMessage(updateMe.error) }) })] })] }));
}
function getMutationErrorMessage(error) {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === "object" &&
        error !== null &&
        "message" in error &&
        typeof error.message === "string") {
        return error.message;
    }
    // TODO Handle server errors
    return "Something went wrong. Please try again.";
}
//# sourceMappingURL=edit-user-profile-dialog.js.map